import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isValid } from '@totalsoft/pure-validations-react';
import { initialState, reducer } from 'features/documentTypes/reducers/properties/states/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { documentTypeXPropertyStatesValidator } from 'features/documentTypes/validators';
import DocumentTypePropertyStatesListComponent from './DocumentTypePropertyStatesListComponent';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { DICTIONARY_ALLOWED_STATES_QUERY } from '../../queries';
import { ADD_UPDATE_DOCUMENT_TYPE_PROPERTY_X_STATE } from '../../mutations';
import { DOCUMENT_TYPE_PROPERTY_X_STATE_UPDATED } from '../../subscriptions';

const DocumentTypePropertyStatesContainer = ({ documentTypeId, propertyId, data }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [documentTypeXPropertyStatesValidation, validateDocumentTypeXPropertyStates] = useValidation(documentTypeXPropertyStatesValidator);

  // #region Queries

  const { data: allowedStatesData } = useQuery(DICTIONARY_ALLOWED_STATES_QUERY, {
    variables: { documentTypeId: documentTypeId },
    skip: documentTypeId === undefined || documentTypeId === null
  });

  // #endregion

  // #region Mutations

  const [addOrUpdateDocumentTypePropertyXState] = useMutation(ADD_UPDATE_DOCUMENT_TYPE_PROPERTY_X_STATE);

  // #endregion

  // #region Subscriptions

  useSubscription(DOCUMENT_TYPE_PROPERTY_X_STATE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      client.cache.evict({
        id: `DocumentTypePropertyItem:{"documentTypeId":${data.documentTypePropertyXStateUpdated.documentTypeId},"propertyId":${data.documentTypePropertyXStateUpdated.propertyId}}`
      });
      addToast(t('DocumentTypes.DocumentTypeProperty.States.DocumentTypeXPropertyXDocumentStateUpdated'), 'success');
      dispatch({ type: 'OnDocumentTypeXPropertyXStateUpdated' });
    }
  });

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    dispatch({ type: 'OnDocumentTypeXPropertyXStateAdded', documentTypeId, propertyId });
  };

  const handlePropertyChanged = (propName, propValue, index) => {
    dispatch({ type: 'OnDocumentTypeXPropertyXStateChanged', propName, propValue, index });
  };

  const handleDelete = index => () => {
    if (window.confirm(t('DocumentTypes.DocumentTypeProperty.States.Delete'))) {
      dispatch({ type: 'OnDocumentTypeXPropertyXStateDelete', index });
    }
  };

  const handleSave = () => {
    const input = {
      states: localState.values,
      documentTypeId,
      propertyId
    };

    setSaveDisabled(true);
    return addOrUpdateDocumentTypePropertyXState({ variables: { input } });
  };

  // #endregion

  // #region Side effects

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    validateDocumentTypeXPropertyStates(localState.values);
  }, [localState, validateDocumentTypeXPropertyStates]);

  useEffect(() => {
    setSaveDisabled(!(documentTypeXPropertyStatesValidation |> isValid));
  }, [localState, documentTypeXPropertyStatesValidation]);

  // #endregion

  return (
    <DocumentTypePropertyStatesListComponent
      states={localState}
      allowedStates={allowedStatesData ? allowedStatesData.dictionaryAllowedStates : []}
      onSave={handleSave}
      onAdd={handleAdd}
      onPropertyChanged={handlePropertyChanged}
      onDelete={handleDelete}
      validation={documentTypeXPropertyStatesValidation}
      saveDisabled={saveDisabled}
    />
  );
};

DocumentTypePropertyStatesContainer.propTypes = {
  documentTypeId: PropTypes.number,
  propertyId: PropTypes.number,
  data: PropTypes.array.isRequired
};

export default DocumentTypePropertyStatesContainer;
